import type { ElementType } from '@react-spring/web';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import type {
  ButtonOrLinkComponentProps,
  LinkOrButton,
} from '../../styles/clickable-element/clickable-element';
import {
  getClickableElementStyles,
  isValidIconButton,
  isValidLoadingState,
} from '../../styles/clickable-element/clickable-element';
import type { BoxProps } from '../box/box';
import { Box } from '../box/box';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { ScreenReaderText } from '../screen-reader-text/screen-reader-text';
import type { TextProps } from '../text/text';
import { Text } from '../text/text';

export type ToOrHref =
  | {
      /**
       * the to prop is used when we want the link to work as a router link
       */
      to: string;
      NavLink: ElementType;
      href?: never;
      target?: never;
      preventScrollReset?: boolean;
      // Be careful enabling this on routes which fetch a lot of date in the loader,
      // this can cause performance issues.
      prefetch?: 'intent';
    }
  | {
      /**
       * the href prop is used when we want the link to work outside our app
       */
      href: string;
      NavLink?: never;
      to?: never;
      target?: '_blank';
      preventScrollReset?: never;
      prefetch?: never;
    };

export type LinkProps = Pick<TextProps, 'children' | 'className'> &
  ToOrHref &
  ButtonOrLinkComponentProps & {
    'data-testid'?: string;
  } & Pick<BoxProps, 'ref'>;

const TEST_IDS = {
  LINK: 'link-component',
  LEFT_ICON: 'left-icon',
  RIGHT_ICON: 'right-icon',
};

export const Link = ({
  children,
  'aria-disabled': ariaDisabled,
  disabled = false,
  loading = false,
  size: rawSize,
  LeftIcon,
  RightIcon,
  Icon,
  iconColor,
  variant = LeftIcon || RightIcon || Icon ? 'text-link' : 'text-underline',
  to,
  className,
  preventScrollReset,
  prefetch,
  ref,
  NavLink,
  ...props
}: LinkProps) => {
  // we need to set the default size to inherit for text links
  const size = rawSize
    ? rawSize
    : variant === 'text-link' || variant === 'text-underline'
      ? 'inherit'
      : 'medium';

  const { styles, iconSize, textVariant, padding, spacing, resetStyles } =
    getClickableElementStyles({
      ...({ size, variant } as LinkOrButton),
      disabled: disabled || ariaDisabled,
      hasLeftIcon: !!LeftIcon,
      hasRightIcon: !!RightIcon,
    });

  return (
    <Box
      disabled={disabled}
      aria-disabled={ariaDisabled}
      Component={disabled ? 'span' : to ? NavLink : 'a'}
      variant="initial" // css display property set by getClickableElementStyles
      rounded
      padding={padding}
      to={disabled ? undefined : to}
      data-testid={TEST_IDS.LINK}
      ref={ref}
      target={to ? undefined : '_blank'}
      spacing={spacing}
      prefetch={to ? prefetch : undefined}
      {...(to && !disabled ? { preventScrollReset } : {})} // if we pass `preventScrollReset` to a span we get a React warning - this is a workaround
      {...props}
      className={classFilter([resetStyles, styles, className])}
    >
      {LeftIcon ? (
        <LeftIcon size={iconSize} data-testid={TEST_IDS.LEFT_ICON} />
      ) : null}

      {isValidIconButton(variant, Icon) ? (
        <>
          <ScreenReaderText>{children}</ScreenReaderText>
          <Icon size={iconSize} color={iconColor} />
        </>
      ) : (
        <Text color="currentColor" variant={textVariant}>
          {children}
        </Text>
      )}

      {RightIcon ? (
        <RightIcon size={iconSize} data-testid={TEST_IDS.RIGHT_ICON} />
      ) : null}

      {isValidLoadingState(loading, size) ? (
        <LoadingSpinner
          size={size}
          color={variant === 'solid' ? 'white' : 'primary'}
        />
      ) : null}
    </Box>
  );
};
